import { useState, useEffect, useCallback } from 'react';
import { QUEST_DURATION, calculateProgress } from '../utils/questUtils';

interface UseQuestReturn {
  isStarted: boolean;
  timeRemaining: number;
  progressPercentage: number;
  startQuest: () => void;
  resetQuest: () => void;
  completeQuest: () => void;
}

export const useQuest = (questId?: string): UseQuestReturn => {
  const [isStarted, setIsStarted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(QUEST_DURATION);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load quest state from localStorage on mount
  useEffect(() => {
    if (questId && !isInitialized) {
      const savedState = localStorage.getItem(`quest_${questId}`);
      console.log(`[useQuest] Loading state for ${questId}:`, savedState);
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          setIsStarted(parsedState.isStarted);
          setTimeRemaining(parsedState.timeRemaining);
          console.log(`[useQuest] Loaded state for ${questId}:`, parsedState);
        } catch (error) {
          console.error('Error parsing saved quest state:', error);
        }
      }
      setIsInitialized(true);
    }
  }, [questId, isInitialized]);



  const startQuest = useCallback(() => {
    console.log(`[useQuest] Starting quest: ${questId}`);
    setIsStarted(true);
    setTimeRemaining(QUEST_DURATION);
    
    // Directly save the started state to localStorage
    if (questId) {
      const startedState = {
        isStarted: true,
        timeRemaining: QUEST_DURATION
      };
      localStorage.setItem(`quest_${questId}`, JSON.stringify(startedState));
      console.log(`[useQuest] Directly saved started state for ${questId}:`, startedState);
    }
  }, [questId]);

  const resetQuest = useCallback(() => {
    console.log(`[useQuest] Resetting quest: ${questId}`);
    setIsStarted(false);
    setTimeRemaining(QUEST_DURATION);
    
    // Directly save the reset state to localStorage
    if (questId) {
      const resetState = {
        isStarted: false,
        timeRemaining: QUEST_DURATION
      };
      localStorage.setItem(`quest_${questId}`, JSON.stringify(resetState));
      console.log(`[useQuest] Directly saved reset state for ${questId}:`, resetState);
    }
  }, [questId]);

  const completeQuest = useCallback(() => {
    console.log(`[useQuest] Completing quest: ${questId}`);
    setIsStarted(false);
    setTimeRemaining(0);
    
    // Directly save the completed state to localStorage
    if (questId) {
      const completedState = {
        isStarted: false,
        timeRemaining: 0
      };
      localStorage.setItem(`quest_${questId}`, JSON.stringify(completedState));
      console.log(`[useQuest] Directly saved completed state for ${questId}:`, completedState);
    }
  }, [questId]);

  // Countdown effect
  useEffect(() => {
    if (!isStarted || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsStarted(false); // Quest completed when timer reaches 0
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isStarted, timeRemaining]);

  const progressPercentage = calculateProgress(timeRemaining, isStarted);

  return {
    isStarted,
    timeRemaining,
    progressPercentage,
    startQuest,
    resetQuest,
    completeQuest
  };
}; 